import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';

const NUMBERED_RANGE =
  /\b(last|past|previous)\s+(\d{1,3})\s+(days?|weeks?|months?|quarters?|years?)\b/gi;
const NAMED_RANGE =
  /\b(today|yesterday|this\s+(?:week|month|quarter|year)|(?:last|previous)\s+(?:week|month|quarter|year))\b/gi;

type DateRange = { start: Date; end: Date };

function formatDateRange({ start, end }: DateRange): string {
  if (format(start, `yyyy-MM-dd`) === format(end, `yyyy-MM-dd`)) {
    return format(start, `MMM d, yyyy`);
  }
  const startFormat =
    start.getFullYear() === end.getFullYear() ? `MMM d` : `MMM d, yyyy`;
  return `${format(start, startFormat)} – ${format(end, `MMM d, yyyy`)}`;
}

function getNumberedRange(date: Date, count: number, unit: string): DateRange {
  const normalizedUnit = unit.toLowerCase().replace(/s$/, ``);
  const subtract = {
    day: subDays,
    week: subWeeks,
    month: subMonths,
    quarter: (value: Date, amount: number) => subMonths(value, amount * 3),
    year: subYears,
  }[normalizedUnit];

  return { start: subtract(date, count), end: date };
}

function getNamedRange(date: Date, expression: string): DateRange {
  const normalizedExpression = expression.toLowerCase().replace(/\s+/g, ` `);
  if (normalizedExpression === `today`) return { start: date, end: date };
  if (normalizedExpression === `yesterday`) {
    const yesterday = subDays(date, 1);
    return { start: yesterday, end: yesterday };
  }

  const [, qualifier, unit] = normalizedExpression.match(
    /(this|last|previous) (.+)/,
  );
  const periodDate =
    qualifier === `this`
      ? date
      : {
          week: subWeeks,
          month: subMonths,
          quarter: (value: Date, amount: number) =>
            subMonths(value, amount * 3),
          year: subYears,
        }[unit](date, 1);
  const start = {
    week: startOfWeek,
    month: startOfMonth,
    quarter: startOfQuarter,
    year: startOfYear,
  }[unit](periodDate);
  const end =
    qualifier === `this`
      ? date
      : {
          week: endOfWeek,
          month: endOfMonth,
          quarter: endOfQuarter,
          year: endOfYear,
        }[unit](periodDate);

  return { start, end };
}

export function clarifyRelativeDateRanges(
  text: string,
  createdAt?: number,
  now: Date = new Date(),
): string {
  if (!createdAt || !Number.isFinite(createdAt)) return text;

  const date = new Date(createdAt * 1000);
  if (Number.isNaN(date.getTime()) || Number.isNaN(now.getTime())) return text;

  return text
    .replace(NUMBERED_RANGE, (expression, _qualifier, count, unit) => {
      if (Number(count) < 1) return expression;
      const range = getNumberedRange(date, Number(count), unit);
      const currentRange = getNumberedRange(now, Number(count), unit);
      return formatDateRange(range) === formatDateRange(currentRange)
        ? expression
        : formatDateRange(range);
    })
    .replace(NAMED_RANGE, (expression) => {
      const range = getNamedRange(date, expression);
      const currentRange = getNamedRange(now, expression);
      return formatDateRange(range) === formatDateRange(currentRange)
        ? expression
        : formatDateRange(range);
    });
}

interface MarkdownNode {
  type: string;
  value?: string;
  children?: MarkdownNode[];
}

export function remarkRelativeDateRanges(createdAt?: number, now = new Date()) {
  return (tree: MarkdownNode) => {
    const visit = (node: MarkdownNode) => {
      if (node.type === `text` && node.value) {
        node.value = clarifyRelativeDateRanges(node.value, createdAt, now);
      }
      if (node.type === `link` || node.type === `linkReference`) return;
      node.children?.forEach(visit);
    };

    visit(tree);
  };
}
